import React from "react";
import { PortalAppShell } from "./components/PortalAppShell";
import { LandingPage } from "./components/landing/LandingPage";
import { LocaleProvider } from "./context/LocaleContext";
import { usePortalPath } from "./hooks/usePortalPath";
import { OnboardingPage } from "./pages/OnboardingPage";
import { ComplianceOverviewPage } from "./pages/ComplianceOverviewPage";
import { GuaranteesPage } from "./pages/GuaranteesPage";
import { AiLabelsPage } from "./pages/AiLabelsPage";
import { WithdrawalsPage } from "./pages/WithdrawalsPage";
import { WithdrawalDetailPage } from "./pages/WithdrawalDetailPage";
import { SettingsPage } from "./pages/SettingsPage";
import { EmailsPage } from "./pages/EmailsPage";
import { ExtensionTextsPage } from "./pages/ExtensionTextsPage";
import { ExportsPage } from "./pages/ExportsPage";
import { HelpPage } from "./pages/HelpPage";
import { BillingPage } from "./pages/BillingPage";
import { ForgotPasswordPage, ResetPasswordPage, SignInPage, SignUpPage, VerifyEmailPage } from "./pages/AuthPage";

function isAuthenticated() {
  const root = document.getElementById("portal-root");
  return root?.dataset.authenticated === "true";
}

function isLandingPreview() {
  const root = document.getElementById("portal-root");
  return root?.dataset.landingPreview === "true";
}

function authView() {
  const root = document.getElementById("portal-root");
  return root?.dataset.authView || "";
}

function AuthRouter({ path }) {
  const view = authView();
  if (view === "signin" || path === "/portal/signin" || path === "/users/sign_in") return <SignInPage />;
  if (view === "signup" || path === "/portal/signup" || path === "/users/sign_up" || path === "/users") return <SignUpPage />;
  if (view === "forgot" || path === "/portal/forgot-password" || path === "/users/password/new") return <ForgotPasswordPage />;
  if (view === "verify" || path === "/portal/verify-email") return <VerifyEmailPage />;
  if (view === "reset" || path === "/portal/reset-password" || path === "/users/password/edit") return <ResetPasswordPage />;
  return null;
}

function AdminRouter({ path }) {
  if (path.includes("/portal/overview")) return <ComplianceOverviewPage />;
  if (path.includes("/portal/onboarding")) return <OnboardingPage />;
  if (/\/portal\/withdrawals\/\d+/.test(path)) return <WithdrawalDetailPage path={path} />;
  if (path.includes("/portal/withdrawals")) return <WithdrawalsPage />;
  if (path.includes("/portal/guarantees")) return <GuaranteesPage />;
  if (path.includes("/portal/ai-labels")) return <AiLabelsPage />;
  if (path.includes("/portal/emails")) return <EmailsPage />;
  if (path.includes("/portal/extension-texts")) return <ExtensionTextsPage />;
  if (path.includes("/portal/settings")) return <SettingsPage />;
  if (path.includes("/portal/exports")) return <ExportsPage />;
  if (path.includes("/portal/billing")) return <BillingPage />;
  if (path.includes("/portal/help")) return <HelpPage />;
  return <ComplianceOverviewPage />;
}

export function App() {
  const path = usePortalPath();
  const authenticated = isAuthenticated();
  const landingPreview = isLandingPreview();
  const isLanding = path === "/portal" || path === "/portal/";

  const authPage = AuthRouter({ path });
  if (authPage) return authPage;

  if (landingPreview) {
    return <LandingPage />;
  }

  if (isLanding) {
    return <LandingPage authenticated={authenticated} />;
  }

  return (
    <PortalAppShell path={path}>
      <AdminRouter path={path} />
    </PortalAppShell>
  );
}

export function AppWithProviders() {
  return (
    <LocaleProvider>
      <App />
    </LocaleProvider>
  );
}
